import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "../../redux/store";

import {
  submitClassifiedProductReview,
  fetchClassifiedReviewByOrder,
} from "../../redux/classifiedRatingSlice";

import {
  listenToClassifiedData,
} from "../../redux/classifiedsSlice";

const criteriaList = [
  "Quality",
  "Delivery Speed",
  "Accuracy",
  "Communication",
  "Value",
];

const MetaProductReview: React.FC = () => {
  const { guestId, listingId, orderId } = useParams<{
    guestId: string;
    listingId: string;
    orderId: string;
  }>();

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const listing = useSelector(
    (state: RootState) => state.classifieds.currentListing
  );

  /* ───────── STATE ───────── */

  const [ratings, setRatings] = useState<Record<string, number>>({});
  const [reviewText, setReviewText] = useState("");
  const [trueToSize, setTrueToSize] = useState(false);
  const [damaged, setDamaged] = useState(false);
  const [wouldRecommend, setWouldRecommend] = useState(true);

  const [reviewed, setReviewed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  /* ───────── LOAD LISTING DATA ───────── */

  useEffect(() => {
    if (listingId) {
      dispatch(listenToClassifiedData(listingId));
    }
  }, [dispatch, listingId]);

  /* ───────── LOAD EXISTING REVIEW ───────── */

  useEffect(() => {
    if (listingId && orderId && guestId) {
      dispatch(
        fetchClassifiedReviewByOrder({
          listingId,
          orderId,
          guestId,
        })
      )
        .unwrap()
        .then((existing) => {
          if (!existing) return;

          setReviewed(true);
          setReviewText(existing.reviewText ?? "");

          setRatings({
            Quality: existing.quality ?? 0,
            "Delivery Speed": existing.deliverySpeed ?? 0,
            Accuracy: existing.accuracy ?? 0,
            Communication: existing.communication ?? 0,
            Value: existing.value ?? 0,
          });

          setTrueToSize(existing.trueToSize ?? false);
          setDamaged(existing.damaged ?? false);
          setWouldRecommend(existing.wouldRecommend ?? false);
        })
        .catch(() => {});
    }
  }, [dispatch, listingId, orderId, guestId]);

  /* ───────── HANDLERS ───────── */

  const handleRating = (category: string, value: number) => {
    if (reviewed) return;

    setRatings((prev) => ({
      ...prev,
      [category]: value,
    }));
  };

  const overallRating = () => {
    const values = Object.values(ratings);
    if (values.length < criteriaList.length) return 0;

    return parseFloat(
      (values.reduce((a, b) => a + b, 0) / values.length).toFixed(1)
    );
  };

  const isReady =
    reviewText.trim() &&
    Object.keys(ratings).length === criteriaList.length;

  const submit = async () => {
    if (!listingId || !guestId || !orderId || !listing) return;

    setSubmitting(true);

    try {
      await dispatch(
        submitClassifiedProductReview({
          listingId,
          hostId: listing.hostId,
          guestId,
          orderId,
          rating: overallRating(),
          reviewText,
          criteria: {
            quality: ratings["Quality"],
            deliverySpeed: ratings["Delivery Speed"],
            accuracy: ratings["Accuracy"],
            communication: ratings["Communication"],
            value: ratings["Value"],
          },
          trueToSize,
          damaged,
          wouldRecommend,
        })
      ).unwrap();

      setReviewed(true);
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  /* ───────── UI ───────── */

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <h2 style={styles.heading}>Product Review</h2>

        {listing && (
          <div style={styles.productPreview}>
            <img
              src={listing.photos?.[0] || "/default-product.jpg"}
              style={styles.previewImg}
              alt=""
            />
            <div>
              <h4 style={{ margin: 0 }}>{listing.name}</h4>
            </div>
          </div>
        )}

        {criteriaList.map((label) => (
          <div key={label} style={styles.ratingRow}>
            <span>{label}</span>
            <div style={styles.stars}>
              {[1, 2, 3, 4, 5].map((num) => (
                <span
                  key={num}
                  style={{
                    ...styles.star,
                    background:
                      ratings[label] >= num ? "#111" : "transparent",
                    color:
                      ratings[label] >= num ? "#fff" : "#111",
                    cursor: reviewed ? "default" : "pointer",
                  }}
                  onClick={() => handleRating(label, num)}
                >
                  ★
                </span>
              ))}
            </div>
          </div>
        ))}

        <textarea
          placeholder="Write your experience..."
          value={reviewText}
          onChange={(e) => setReviewText(e.target.value)}
          disabled={reviewed}
          style={styles.textarea}
        />

        <div style={styles.switchRow}>
          <label>
            <input
              type="checkbox"
              checked={trueToSize}
              disabled={reviewed}
              onChange={() => setTrueToSize(!trueToSize)}
            />
            True to Size
          </label>

          <label>
            <input
              type="checkbox"
              checked={damaged}
              disabled={reviewed}
              onChange={() => setDamaged(!damaged)}
            />
            Item Damaged
          </label>

          <label>
            <input
              type="checkbox"
              checked={wouldRecommend}
              disabled={reviewed}
              onChange={() =>
                setWouldRecommend(!wouldRecommend)
              }
            />
            Would Recommend
          </label>
        </div>

        <p style={styles.overall}>
          Overall Rating: {overallRating() || "-"}
        </p>

        <button
          disabled={!isReady || reviewed || submitting}
          style={{
            ...styles.primaryBtn,
            opacity: isReady && !reviewed ? 1 : 0.4,
          }}
          onClick={submit}
        >
          {submitting
            ? "Submitting..."
            : reviewed
            ? "Already Reviewed"
            : "Submit Review"}
        </button>

        <button
          style={styles.secondaryBtn}
          onClick={() => navigate("/")}
        >
          Back Home
        </button>
      </div>
    </div>
  );
};

export default MetaProductReview;




const styles: { [key: string]: React.CSSProperties } = {
  page: {
    background: "#f5f5f5",
    minHeight: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
  card: {
    background: "#fff",
    width: 480,
    borderRadius: 20,
    padding: 28,
    boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
    display: "flex",
    flexDirection: "column",
    gap: 18,
  },
  heading: {
    textAlign: "center",
    fontSize: 22,
    fontWeight: 700,
  },
  productPreview: {
    display: "flex",
    gap: 12,
    alignItems: "center",
  },
  previewImg: {
    width: 70,
    height: 70,
    borderRadius: 12,
    objectFit: "cover",
  },
  ratingRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  stars: {
    display: "flex",
    gap: 6,
  },
  star: {
    width: 30,
    height: 30,
    borderRadius: 8,
    border: "1px solid #ddd",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
  },
  textarea: {
    minHeight: 90,
    borderRadius: 12,
    padding: 12,
    border: "1px solid #ddd",
    resize: "none",
  },
  switchRow: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  overall: {
    textAlign: "center",
    fontWeight: 600,
  },
  primaryBtn: {
    background: "#111",
    color: "#fff",
    padding: "12px",
    borderRadius: 14,
    border: "none",
    fontWeight: 600,
    cursor: "pointer",
  },
  secondaryBtn: {
    background: "transparent",
    border: "1px solid #ddd",
    padding: "10px",
    borderRadius: 14,
    cursor: "pointer",
  },
};
